import { supabase } from "@/integrations/supabase/client";

export type StripeEnv = "sandbox" | "live";

type CheckoutSessionResult = { clientSecret: string } | { error: string };

const BACKEND_BASE = (import.meta.env.VITE_PAYMENTS_API_BASE as string | undefined)?.replace(
  /\/$/,
  "",
);

export async function createApplicationCheckout({
  data,
}: {
  data: {
    applicationId: string;
    returnUrl: string;
    environment: StripeEnv;
    couponCode?: string;
  };
}): Promise<CheckoutSessionResult> {
  if (
    !/^[0-9a-fA-F-]{36}$/.test(data.applicationId) ||
    (data.environment !== "sandbox" && data.environment !== "live")
  ) {
    throw new Error("Invalid input");
  }
  if (data.couponCode !== undefined && data.couponCode.length > 40) {
    throw new Error("Invalid input");
  }

  const auth = await supabase.auth.getSession();
  const token = auth.data.session?.access_token;
  if (!token) return { error: "Necesitas iniciar sesión para procesar el pago." };

  if (!BACKEND_BASE) {
    return {
      error:
        "Pagos requieren un backend. Configura VITE_PAYMENTS_API_BASE con la URL del endpoint de Stripe (ej. Edge Function de Supabase).",
    };
  }

  try {
    const response = await fetch(`${BACKEND_BASE}/checkout-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      let message = `Payment backend returned ${response.status}`;
      try {
        const err = await response.json();
        if (err && typeof err.error === "string") message = err.error;
      } catch {
        /* ignore */
      }
      return { error: message };
    }
    const result = (await response.json()) as CheckoutSessionResult;
    if ("clientSecret" in result || ("error" in result && typeof result.error === "string")) {
      return result;
    }
    return { error: "Respuesta inválida del backend de pagos." };
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}
